import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Client {
  id: string
  client_code: string
  company_name: string
  industry: string
  tier: string
  status: string
  primary_contact: string
  email: string
  contract_value: number
  renewal_date: string
  satisfaction_score: number
  account_manager: string
}

interface Ticket {
  id: string
  ticket_number: string
  client_name: string
  subject: string
  priority: string
  status: string
  created_date: string
}

export default function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([])
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [clientRes, ticketRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/clients/'),
      fetch(API_BASE + '/clients/tickets'),
      fetch(API_BASE + '/clients/statistics')
    ])
    if (clientRes.ok) setClients((await clientRes.json()).clients || [])
    if (ticketRes.ok) setTickets((await ticketRes.json()).tickets || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'resolved': case 'closed': return 'bg-green-100 text-green-800'
      case 'open': case 'in_progress': case 'prospect': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'inactive': case 'urgent': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'enterprise': return 'bg-purple-100 text-purple-800'
      case 'professional': return 'bg-blue-100 text-blue-800'
      case 'starter': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Client Portal</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Client</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Clients</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_clients}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Enterprise</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.enterprise_clients}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Contract Value</div>
              <div className="text-2xl font-bold text-blue-600">${(statistics.total_contract_value / 1000000).toFixed(1)}M</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Satisfaction</div>
              <div className="text-2xl font-bold">{statistics.avg_satisfaction}/5</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Tickets</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.open_tickets}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Clients</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clients.slice(0, 12).map(client => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{client.company_name}</div>
                      <div className="flex gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${getTierColor(client.tier)}`}>{client.tier}</span>
                        <span className="text-xs text-gray-500 capitalize">{client.industry}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{client.primary_contact}</div>
                      <div className="text-xs text-gray-500">{client.account_manager}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">${client.contract_value.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">Renewal: {client.renewal_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(client.status)}`}>{client.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Support Tickets</h3>
            <div className="space-y-3">
              {tickets.slice(0, 10).map(ticket => (
                <div key={ticket.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{ticket.ticket_number}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(ticket.status)}`}>{ticket.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{ticket.subject}</div>
                  <div className="text-xs text-gray-600 mt-1">{ticket.client_name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
